<template>
    <b-modal :active="active" has-modal-card trap-focus @close="close" @update:active="v => !v && close()">
        <div class="modal-card" style="max-width: 520px; width: auto;">
            <header class="modal-card-head">
                <p class="modal-card-title is-size-5"><i class="far fa-briefcase mr-2"></i>Contracted Work Schedule</p>
            </header>
            <section class="modal-card-body">
                <div class="intro mb-4">
                    <i class="fad fa-circle-info has-text-info mt-1"></i>
                    <p class="mb-0">
                        Enter the hours you're contracted to work each week. Study time may not be
                        logged during these hours. Your first declaration also covers your existing
                        study log; changes after that apply going forward only.
                    </p>
                </div>
                <div class="sched-rows">
                    <div v-for="(day, dow) in editDays" :key="dow" class="sched-row" :class="{'is-enabled': day.enabled}">
                        <b-checkbox v-model="day.enabled" class="mb-0">
                            <span :class="day.enabled ? 'has-text-weight-semibold' : ''">{{ dowNames[dow] }}</span>
                        </b-checkbox>
                        <div class="sched-times" v-if="day.enabled">
                            <i class="far fa-clock has-text-grey"></i>
                            <b-select v-model="day.start" size="is-small">
                                <option v-for="t in timeOptions" :key="'s' + t.value" :value="t.value">{{ t.label }}</option>
                            </b-select>
                            <span class="has-text-grey">to</span>
                            <b-select v-model="day.end" size="is-small">
                                <option v-for="t in timeOptions" :key="'e' + t.value" :value="t.value">{{ t.label }}</option>
                            </b-select>
                        </div>
                        <div class="sched-times has-text-grey-light" v-else>&mdash;</div>
                    </div>
                </div>
                <div class="year-box mt-4">
                    <b-checkbox v-model="schoolYear">
                        My contract runs only part of the year (school-year contract)
                    </b-checkbox>
                    <div class="year-range mt-2" v-if="schoolYear">
                        <span class="has-text-grey">From</span>
                        <b-select v-model="yearStartMonth" size="is-small">
                            <option v-for="(m, i) in monthOptions" :key="'ysm' + i" :value="m.value">{{ m.label }}</option>
                        </b-select>
                        <b-select v-model="yearStartDay" size="is-small">
                            <option v-for="d in 31" :key="'ysd' + d" :value="pad(d)">{{ d }}</option>
                        </b-select>
                        <span class="has-text-grey">to</span>
                        <b-select v-model="yearEndMonth" size="is-small">
                            <option v-for="(m, i) in monthOptions" :key="'yem' + i" :value="m.value">{{ m.label }}</option>
                        </b-select>
                        <b-select v-model="yearEndDay" size="is-small">
                            <option v-for="d in 31" :key="'yed' + d" :value="pad(d)">{{ d }}</option>
                        </b-select>
                    </div>
                    <p class="has-text-grey mt-1 mb-0" v-if="schoolYear" style="font-size: 0.75rem;">
                        Rough dates are fine — your work schedule only applies between them, repeating each year.
                    </p>
                </div>
                <b-message type="is-danger" v-if="error" class="mt-4 mb-0">{{ error }}</b-message>
                <div class="attest-box mt-4">
                    <i class="fad fa-file-signature"></i>
                    <b-checkbox v-model="attest" class="attest-check">
                        By supplying my contracted work schedule, I confirm that my certification does
                        not allow study time to be logged during these hours, except during prep periods
                        or other eligible time during my day.
                    </b-checkbox>
                </div>
            </section>
            <footer class="modal-card-foot is-justify-content-flex-end">
                <b-button @click="close">Cancel</b-button>
                <b-button type="is-success" :loading="isSaving" :disabled="!attest" @click="save">Save Schedule</b-button>
            </footer>
        </div>
    </b-modal>
</template>

<script>
import {axiosInstance as http} from '@/http_client';
import moment from 'moment';
import {DOW_NAMES} from './scheduleUtil';

export default {
    name: 'ContractedScheduleModal',
    props: {
        active: {type: Boolean, default: false},
        // current schedule object from the API ({declared, blocks, ...}) or null
        schedule: {type: Object, default: null},
    },
    data() {
        return {
            isSaving: false,
            error: null,
            attest: false,
            dowNames: DOW_NAMES,
            editDays: DOW_NAMES.map(() => ({enabled: false, start: '09:00', end: '17:00'})),
            schoolYear: false,
            yearStartMonth: '08',
            yearStartDay: '25',
            yearEndMonth: '06',
            yearEndDay: '15',
        };
    },
    computed: {
        monthOptions() {
            return moment.monthsShort().map((label, i) => ({label, value: String(i + 1).padStart(2, '0')}));
        },
        timeOptions() {
            const out = [];
            for (let m = 0; m < 24 * 60; m += 30) {
                const hh = String(Math.floor(m / 60)).padStart(2, '0');
                const mm = String(m % 60).padStart(2, '0');
                out.push({value: `${hh}:${mm}`, label: moment(`${hh}:${mm}`, 'HH:mm').format('h:mma')});
            }
            return out;
        },
    },
    watch: {
        active(open) {
            if (open) this.reset();
        },
    },
    methods: {
        pad(n) {
            return String(n).padStart(2, '0');
        },
        reset() {
            this.error = null;
            this.attest = false;
            const blocks = (this.schedule && this.schedule.blocks) || [];
            this.editDays = DOW_NAMES.map((name, dow) => {
                const block = blocks.find(b => b.dow === dow);
                return block
                    ? {enabled: true, start: block.start, end: block.end}
                    : {enabled: false, start: '09:00', end: '17:00'};
            });
            const ys = this.schedule && this.schedule.year_start;
            const ye = this.schedule && this.schedule.year_end;
            this.schoolYear = !!(ys && ye);
            if (this.schoolYear) {
                [this.yearStartMonth, this.yearStartDay] = ys.split('-');
                [this.yearEndMonth, this.yearEndDay] = ye.split('-');
            } else {
                this.yearStartMonth = '08'; this.yearStartDay = '25';
                this.yearEndMonth = '06'; this.yearEndDay = '15';
            }
        },
        close() {
            this.$emit('update:active', false);
        },
        async save() {
            this.error = null;
            const blocks = [];
            for (let dow = 0; dow < 7; dow++) {
                const day = this.editDays[dow];
                if (!day.enabled) continue;
                if (day.end <= day.start) {
                    this.error = `${DOW_NAMES[dow]}: end time must be after start time.`;
                    return;
                }
                blocks.push({dow, start: day.start, end: day.end});
            }
            this.isSaving = true;
            try {
                const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
                const year_start = this.schoolYear ? `${this.yearStartMonth}-${this.yearStartDay}` : null;
                const year_end = this.schoolYear ? `${this.yearEndMonth}-${this.yearEndDay}` : null;
                const {data} = await http.post('app/study-activity/schedule', {timezone, blocks, attest: this.attest, year_start, year_end});
                this.$emit('saved', data);
                this.close();
            } catch (err) {
                this.error = (err.response && err.response.data) || 'Failed to save schedule.';
            }
            this.isSaving = false;
        },
    },
};
</script>

<style scoped>
/* one body size; everything inherits so day labels, times, and the
   attestation all read at the same scale */
.modal-card-body {
    font-size: 0.875rem;
}
.modal-card-body >>> .b-checkbox.checkbox {
    font-size: inherit;
}
.intro {
    display: flex;
    gap: 10px;
    align-items: flex-start;
}
.intro i {
    font-size: 1.1rem;
    flex: none;
}
.sched-rows {
    border: 1px solid #ededed;
    border-radius: 8px;
    overflow: hidden;
}
.sched-row {
    display: grid;
    grid-template-columns: 140px 1fr;
    align-items: center;
    min-height: 40px;
    padding: 4px 12px;
}
.sched-row + .sched-row {
    border-top: 1px solid #f2f2f2;
}
.sched-row.is-enabled {
    background: #f6fafe;
}
.sched-row .b-checkbox {
    white-space: nowrap;
}
.sched-times {
    display: flex;
    align-items: center;
    gap: 8px;
}
.sched-times >>> .select select {
    font-size: 0.875rem;
}
.year-range {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.year-range >>> .select select {
    font-size: 0.875rem;
}
/* attestation reads as a signed statement, not another paragraph */
.attest-box {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    background: #fafafa;
    border: 1px solid #ededed;
    border-radius: 8px;
    padding: 12px 14px;
}
.attest-box > i {
    font-size: 1.4rem;
    color: #7a7a7a;
    flex: none;
    margin-top: 2px;
}
/* multi-line label: pin the checkbox to the first line, not vertical center */
.attest-check {
    align-items: flex-start;
}
.attest-check >>> .check {
    margin-top: 3px;
}
</style>
